/**
 * DemocratCharts — backtest statistics.
 *
 * Pure functions over a closed-trades list. The trading panel calls these
 * to compute win rate, profit factor, drawdown, expectancy, etc.
 *
 * Convention:
 *   - P&L is in price-units × quantity (signed). Positive = win.
 *   - Open trades (result=open) are excluded from stats.
 */

export type TradeResult = "open" | "win" | "loss" | "breakeven" | "manual";

export interface TradeForStats {
  result: TradeResult;
  pnl: number | null;
  entry_ts: string;
  exit_ts: string | null;
}

export interface BacktestStats {
  total: number;
  wins: number;
  losses: number;
  breakeven: number;

  winRate: number;        // %
  avgWin: number;         // mean positive P&L
  avgLoss: number;        // mean negative P&L (signed, will be ≤ 0)
  largestWin: number;
  largestLoss: number;

  netPnl: number;
  profitFactor: number;   // sum(wins) / sum(|losses|), Infinity if no losses
  expectancy: number;     // mean P&L per trade
  maxDrawdown: number;    // peak-to-trough on the cumulative P&L curve
  maxDrawdownPct: number; // % of peak equity
}

export const EMPTY_STATS: BacktestStats = {
  total: 0,
  wins: 0,
  losses: 0,
  breakeven: 0,
  winRate: 0,
  avgWin: 0,
  avgLoss: 0,
  largestWin: 0,
  largestLoss: 0,
  netPnl: 0,
  profitFactor: 0,
  expectancy: 0,
  maxDrawdown: 0,
  maxDrawdownPct: 0,
};

export function computeStats(trades: TradeForStats[], startingBalance = 100_000): BacktestStats {
  const closed = trades.filter((t) => t.result !== "open" && t.pnl !== null) as
    (TradeForStats & { pnl: number })[];

  if (closed.length === 0) return EMPTY_STATS;

  // Sort by exit time for proper equity-curve order
  const sorted = [...closed].sort((a, b) => {
    const ax = a.exit_ts ? new Date(a.exit_ts).getTime() : 0;
    const bx = b.exit_ts ? new Date(b.exit_ts).getTime() : 0;
    return ax - bx;
  });

  const wins = sorted.filter((t) => t.pnl > 0);
  const losses = sorted.filter((t) => t.pnl < 0);
  const evens = sorted.filter((t) => t.pnl === 0);

  const sumWins = wins.reduce((acc, t) => acc + t.pnl, 0);
  const sumLosses = losses.reduce((acc, t) => acc + t.pnl, 0); // negative
  const netPnl = sumWins + sumLosses;

  const avgWin = wins.length ? sumWins / wins.length : 0;
  const avgLoss = losses.length ? sumLosses / losses.length : 0;

  const largestWin = wins.length ? Math.max(...wins.map((t) => t.pnl)) : 0;
  const largestLoss = losses.length ? Math.min(...losses.map((t) => t.pnl)) : 0;

  const profitFactor = losses.length
    ? sumWins / Math.abs(sumLosses)
    : (wins.length ? Infinity : 0);

  const expectancy = netPnl / sorted.length;

  // Equity curve & max drawdown
  let equity = startingBalance;
  let peak = startingBalance;
  let maxDD = 0;
  let maxDDPct = 0;
  for (const t of sorted) {
    equity += t.pnl;
    if (equity > peak) peak = equity;
    const dd = peak - equity;
    if (dd > maxDD) {
      maxDD = dd;
      maxDDPct = (dd / peak) * 100;
    }
  }

  return {
    total: sorted.length,
    wins: wins.length,
    losses: losses.length,
    breakeven: evens.length,
    winRate: (wins.length / sorted.length) * 100,
    avgWin,
    avgLoss,
    largestWin,
    largestLoss,
    netPnl,
    profitFactor,
    expectancy,
    maxDrawdown: maxDD,
    maxDrawdownPct: maxDDPct,
  };
}

/** Suggest position size from risk %. Returns quantity (rounded down to whole units). */
export function suggestPositionSize(
  accountBalance: number,
  riskPercent: number,
  slDistance: number
): number {
  if (!Number.isFinite(accountBalance) || accountBalance <= 0) return 0;
  if (!Number.isFinite(riskPercent) || riskPercent <= 0) return 0;
  if (!Number.isFinite(slDistance) || slDistance <= 0) return 0;
  const riskDollars = (accountBalance * riskPercent) / 100;
  return Math.max(0, Math.floor(riskDollars / slDistance));
}

/** Live P&L for an open position at the current price. */
export function livePnl(
  direction: "long" | "short",
  entryPrice: number,
  currentPrice: number,
  quantity: number
): { pnl: number; pnlPct: number } {
  const delta = direction === "long"
    ? currentPrice - entryPrice
    : entryPrice - currentPrice;
  const pnl = delta * quantity;
  const pnlPct = (delta / entryPrice) * 100;
  return { pnl, pnlPct };
}

/** R:R ratio given entry, SL, TP. Returns 0 if SL invalid. */
export function rrRatio(
  direction: "long" | "short",
  entry: number,
  sl: number | null,
  tp: number | null
): number {
  if (sl === null || tp === null) return 0;
  const risk = Math.abs(entry - sl);
  const reward = Math.abs(tp - entry);
  if (risk === 0) return 0;
  // Validate direction consistency (SL/TP on correct side)
  if (direction === "long" && (sl >= entry || tp <= entry)) return 0;
  if (direction === "short" && (sl <= entry || tp >= entry)) return 0;
  return reward / risk;
}
